import React, { useEffect, useRef, useState } from "react";
import SwiperModule from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import { ChevronLeft, ChevronRight } from "shared/assets/icons";
import { Modal, CardProps, IconButton } from "shared/components";
import { StyledGalleryModal, StyledGalleryModalNavigation } from "./styles";

import "swiper/swiper-bundle.min.css";
import { ITheme } from "shared/types";

interface GalleryModalProps extends CardProps {
  items: JSX.Element[];
  handleClose: () => void;
  initIndex?: number;
}

export const GalleryModal: React.FC<GalleryModalProps> = ({
  title,
  icon,
  items,
  handleClose,
  initIndex = 0,
}) => {
  const swiper = useRef<SwiperModule | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(initIndex);

  useEffect(() => {
    const goToPrevNext = (event: KeyboardEvent) => {
      switch (event.code) {
        case "KeyH":
        case "ArrowLeft": {
          swiper.current?.slidePrev();
          break;
        }
        case "KeyL":
        case "ArrowRight": {
          swiper.current?.slideNext();
          break;
        }
      }
    };

    document.addEventListener("keydown", goToPrevNext);

    return () => document.removeEventListener("keydown", goToPrevNext);
  }, []);

  // don't use the lazy loading swiper module as it loads images twice
  const lazyLoadImage = () => {
    // load images for active slide and prev/next slides
    // for a better user experience
    const activeSlides = document.querySelectorAll(
      ".swiper-slide-active, .swiper-slide-prev, .swiper-slide-next"
    );

    if (!activeSlides.length) return;

    activeSlides.forEach(activeSlide => {
      const lazyImage = activeSlide.querySelector("[data-background]") as HTMLElement;

      if (lazyImage) {
        const backgroundImage = lazyImage?.dataset.background;

        if (!backgroundImage) return;

        // set the background-image
        lazyImage.style.backgroundImage = `url(${backgroundImage})`;

        setTimeout(() => {
          // delete the data-background attribute
          delete lazyImage?.dataset.background;
        }, 300);
      }
    });
  };

  return (
    <Modal
      title={title}
      icon={icon}
      handleClose={handleClose}
      isFullscreen
      data-testid="gallery_modal"
    >
      <StyledGalleryModal>
        <Swiper
          preventInteractionOnTransition
          slidesPerView={1}
          initialSlide={initIndex}
          onInit={lazyLoadImage}
          onTransitionEnd={lazyLoadImage}
          onSlideChange={s => setActiveIndex(s.activeIndex)}
          onSwiper={s => (swiper.current = s)}
        >
          {items.map((item, index) => {
            return <SwiperSlide key={index}>{item}</SwiperSlide>;
          })}
        </Swiper>
      </StyledGalleryModal>
      <StyledGalleryModalNavigation>
        <IconButton
          label="prev"
          icon={ChevronLeft}
          size="l"
          onClick={() => swiper.current?.slidePrev()}
          disabled={activeIndex === 0}
          theme={activeIndex === 0 ? ITheme.tertiary : ITheme.primary}
          data-testid="gallery_modal_prev"
        />
        <IconButton
          label="next"
          icon={ChevronRight}
          size="l"
          onClick={() => swiper.current?.slideNext()}
          disabled={activeIndex === items.length - 1}
          theme={activeIndex === items.length - 1 ? ITheme.tertiary : ITheme.primary}
          data-testid="gallery_modal_next"
        />
      </StyledGalleryModalNavigation>
    </Modal>
  );
};
